import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

export function formatFlightDuration(dep: string | Date, arr: string | Date): string {
  const totalMinutes = Math.round((new Date(arr).getTime() - new Date(dep).getTime()) / 60000)
  const hours = Math.floor(totalMinutes / 60)
  const mins = totalMinutes % 60
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

export function formatCountdown(ms: number): string {
  const totalMinutes = Math.max(0, Math.floor(ms / 60000))
  const hours = Math.floor(totalMinutes / 60)
  if (hours >= 24) {
    const days = Math.floor(hours / 24)
    return `${days}d ${hours % 24}h`
  }
  const mins = totalMinutes % 60
  if (hours === 0) return `${mins}m`
  return `${hours}h ${mins}m`
}

export function formatLocalTime(utcDate: string | Date, tz?: string | null): string {
  return dayjs.utc(utcDate).tz(tz ?? 'UTC').format('HH:mm')
}

export function formatLocalDate(utcDate: string | Date, tz?: string | null): string {
  return dayjs.utc(utcDate).tz(tz ?? 'UTC').format('D MMM')
}

// "UTC+4", "UTC-5:30", "UTC+0". Offset depends on the date (DST), hence utcDate.
export function formatUtcOffsetLabel(utcDate: string | Date, tz?: string | null): string {
  const offsetMin = dayjs.utc(utcDate).tz(tz ?? 'UTC').utcOffset()
  const sign = offsetMin < 0 ? '-' : '+'
  const abs = Math.abs(offsetMin)
  const hours = Math.floor(abs / 60)
  const mins = abs % 60
  return `UTC${sign}${hours}${mins ? `:${String(mins).padStart(2, '0')}` : ''}`
}

// Returns the maintenance record the flight's DEPARTURE date falls inside, or null.
// A window is [startDate, endDate) — endDate (start + durationDays) is the first
// AVAILABLE day, so maintenance May 1 for 5 days blocks May 1–5 and frees May 6.
// Compares calendar days: the record's start/end are UTC-midnight day markers,
// while departureDate is a browser-local-midnight Date from DateInput, so each is
// formatted in its own zone to recover the intended calendar day.
export function findMaintenanceConflict<
  T extends { startDate: string | Date; endDate: string | Date },
>(records: T[], departureDate?: Date | null): T | null {
  if (!departureDate) return null
  const depStr = dayjs(departureDate).format('YYYY-MM-DD')
  return (
    records.find((r) => {
      const startStr = dayjs.utc(r.startDate).format('YYYY-MM-DD')
      const endStr = dayjs.utc(r.endDate).format('YYYY-MM-DD')
      return startStr <= depStr && depStr < endStr
    }) ?? null
  )
}
